import { Req, Res } from '@cbo/shared-library';
import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import {
  BaseCustomMetricResponse,
  CreateUpdateGlMappingRequest,
  CustomMetricData,
  CustomMetricSaveResponse,
  CustomReportData,
  CustomReportExportResponse,
  CustomReportId,
  CustomReportMetadata,
  DeleteCustomReportResult,
  DeleteScheduleResponse,
  ExecuteCustomReportRequest,
  JobCodeMappingId,
  PublishReportData,
  ScheduleReportsResponse,
  UpdatePublishReportData,
  UpdateScheduleReportsResponse,
  useAdminRequests,
} from './index';
import { useUsers } from '../../contexts/userContext';
import { IApiError } from '../../services/types';

/**
 * USER PREFERENCES
 */
export const useUserPreferencesMutation = (): UseMutationResult<
  Res.Admin.UserPreferences,
  unknown,
  { preferences: Req.Admin.UserPreferences },
  unknown
> => {
  const { updateUserPreferences } = useAdminRequests();
  const queryClient = useQueryClient();
  const user = useUsers();

  return useMutation({
    mutationKey: ['updateUserPreferences', user.org?.bslId],
    mutationFn: (variables: { preferences: Req.Admin.UserPreferences }) => updateUserPreferences(variables.preferences),
    retry: 1,
    onMutate: async (variables) => {
      const queryKey = ['userPreferences', user.org?.bslId];

      await queryClient.cancelQueries({ queryKey });

      const previousData = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (old: Res.Admin.UserPreferences | undefined) => ({
        ...old,
        favoriteReports: variables.preferences.favoriteReports,
      }));

      return { previousData, queryKey };
    },

    onError: (_error, _variables, context) => {
      if (context?.previousData && context?.queryKey) {
        queryClient.setQueryData(context.queryKey, context.previousData);
      }
    },
    onSettled: (_data, _error, _variables, context) => {
      if (context?.queryKey) {
        queryClient.invalidateQueries({ queryKey: context.queryKey });
      }
    },
    useErrorBoundary: false,
  });
};

/**
 * COMPANY LINKS
 */
export const useCompanyLinkUpdateMutation = (): UseMutationResult<
  Res.Firebase.Link,
  unknown,
  { linkToEdit: Req.Firebase.Link },
  unknown
> => {
  const { editCompanyLink } = useAdminRequests();

  return useMutation((variables: { linkToEdit: Req.Firebase.Link }) => editCompanyLink(variables.linkToEdit), {
    useErrorBoundary: false,
  });
};

export const useCompanyLinkCreateMutation = (): UseMutationResult<
  Res.Firebase.Link,
  unknown,
  { newLink: Req.Firebase.LinkData },
  unknown
> => {
  const { addNewCompanyLink } = useAdminRequests();

  return useMutation((variables: { newLink: Req.Firebase.LinkData }) => addNewCompanyLink(variables.newLink), {
    useErrorBoundary: false,
  });
};

export const useCompanyLinkDeleteMutation = (): UseMutationResult<string, unknown, { linkId: string }, unknown> => {
  const { deleteCompanyLink } = useAdminRequests();

  return useMutation((variables: { linkId: string }) => deleteCompanyLink(variables.linkId), {
    useErrorBoundary: false,
  });
};

export const useNotificationSettingsDeleteMutation = (): UseMutationResult<
  { rowsAffected: number },
  unknown,
  { notificationSettingId: string },
  unknown
> => {
  const { deleteNotificationSettingsData } = useAdminRequests();

  return useMutation(
    (variables: { notificationSettingId: string }) => deleteNotificationSettingsData(variables.notificationSettingId),
    {
      useErrorBoundary: false,
    }
  );
};

export const useNotificationSettingsCreateMutation = (): UseMutationResult<
  Res.Notifications.NotificationSettings,
  unknown,
  { newNotificationSetting: Req.Notifications.NotificationSettingsData },
  unknown
> => {
  const { createNotificationSettingsData } = useAdminRequests();

  return useMutation(
    (variables: { newNotificationSetting: Req.Notifications.NotificationSettingsData }) =>
      createNotificationSettingsData(variables.newNotificationSetting),
    {
      useErrorBoundary: false,
    }
  );
};

export const useNotificationSettingsUpdateMutation = (): UseMutationResult<
  Res.Notifications.NotificationSettings,
  unknown,
  { id: string; notificationSetting: Req.Notifications.NotificationSettingsData },
  unknown
> => {
  const { updateNotificationSettingsData } = useAdminRequests();
  return useMutation(
    (variables: { id: string; notificationSetting: Req.Notifications.NotificationSettingsData }) =>
      updateNotificationSettingsData(variables.id, variables.notificationSetting),
    {
      useErrorBoundary: false,
    }
  );
};

/**
 * Site Group Management
 */
export const useSiteDayPartsUpdateMutation = (): UseMutationResult<
  Res.SiteManagement.SiteViewData,
  unknown,
  { siteDayParts: Req.SiteManagement.DayPart[]; enterpriseUnitId: string },
  unknown
> => {
  const { updateSiteDayParts } = useAdminRequests();
  return useMutation(
    (variables: { siteDayParts: Req.SiteManagement.DayPart[]; enterpriseUnitId: string }) =>
      updateSiteDayParts(variables.siteDayParts, variables.enterpriseUnitId),
    {
      useErrorBoundary: false,
    }
  );
};

/**
 * FISCAL CALENDAR
 */
export const useCreateFiscalCalendarMutation = (): UseMutationResult<
  Res.Calendar.FiscalCalendar,
  unknown,
  { fiscalCalendar: Req.Calendar.FiscalCalendarData },
  unknown
> => {
  const { createFiscalCalendar } = useAdminRequests();

  return useMutation(
    (variables: { fiscalCalendar: Req.Calendar.FiscalCalendarData }) => createFiscalCalendar(variables.fiscalCalendar),
    {
      useErrorBoundary: false,
    }
  );
};

export const useUpdateFiscalCalendarMutation = (): UseMutationResult<
  Res.Calendar.FiscalCalendar,
  unknown,
  { id: string; fiscalCalendar: Req.Calendar.FiscalCalendarData },
  unknown
> => {
  const { updateFiscalCalendar } = useAdminRequests();

  return useMutation(
    (variables: { id: string; fiscalCalendar: Req.Calendar.FiscalCalendarData }) =>
      updateFiscalCalendar(variables.id, variables.fiscalCalendar),
    {
      useErrorBoundary: false,
    }
  );
};

/**
 * PAYROLL CALENDAR
 */
export const usePayrollCalendarUpsertMutation = (): UseMutationResult<
  Res.Calendar.PayrollCalendar,
  unknown,
  { payrollCalendar: Req.Calendar.PayrollCalendarData },
  unknown
> => {
  const { upsertPayrollCalendar } = useAdminRequests();

  return useMutation((variables: { payrollCalendar: Req.Calendar.PayrollCalendarData }) =>
    upsertPayrollCalendar(variables.payrollCalendar)
  );
};

/**
 * GL Mapping
 */
export const useUpdateGlMappingMutation = (): UseMutationResult<
  JobCodeMappingId | undefined,
  unknown,
  { id: string; body: CreateUpdateGlMappingRequest; jobCodeName: string },
  unknown
> => {
  const { updateGlMapping } = useAdminRequests();

  return useMutation((variables: { id: string; body: CreateUpdateGlMappingRequest; jobCodeName: string }) =>
    updateGlMapping(variables.id, variables.body, variables.jobCodeName)
  );
};

export const useCreateGlMappingMutation = (): UseMutationResult<
  JobCodeMappingId | undefined,
  unknown,
  { body: CreateUpdateGlMappingRequest; jobCodeName: string },
  unknown
> => {
  const { createGlMapping } = useAdminRequests();

  return useMutation((variables: { body: CreateUpdateGlMappingRequest; jobCodeName: string }) =>
    createGlMapping(variables.body, variables.jobCodeName)
  );
};

export const useDeleteGlMappingMutation = (): UseMutationResult<
  JobCodeMappingId | undefined,
  unknown,
  { id: string; jobCodeName: string },
  unknown
> => {
  const { deleteGlMapping } = useAdminRequests();

  return useMutation((variables: { id: string; jobCodeName: string }) =>
    deleteGlMapping(variables.id, variables.jobCodeName)
  );
};

/**
 * CUSTOM REPORTS
 */
export const useCustomReportsSaveMutation = (): UseMutationResult<
  CustomReportId,
  unknown,
  { customReport: CustomReportData },
  unknown
> => {
  const { saveCustomReport } = useAdminRequests();

  return useMutation((variables: { customReport: CustomReportData }) => saveCustomReport(variables.customReport));
};

export const useCustomReportDeleteMutation = (): UseMutationResult<
  DeleteCustomReportResult,
  unknown,
  { id: number },
  unknown
> => {
  const { deleteCustomReport } = useAdminRequests();
  return useMutation((variables: { id: number }) => deleteCustomReport(variables.id));
};

export const useExportCustomReportMutation = (): UseMutationResult<
  CustomReportExportResponse | void,
  IApiError,
  ExecuteCustomReportRequest,
  unknown
> => {
  const { exportCustomReport } = useAdminRequests();
  return useMutation((customReport: ExecuteCustomReportRequest) => exportCustomReport(customReport));
};

export const useCustomReportUpdateMutation = (): UseMutationResult<
  CustomReportMetadata,
  unknown,
  { customReport: Partial<CustomReportData>; reportId: number },
  unknown
> => {
  const { updateCustomReport } = useAdminRequests();
  return useMutation((variables: { customReport: Partial<CustomReportData>; reportId: number }) =>
    updateCustomReport(variables.reportId, variables.customReport)
  );
};

/**
 * CUSTOM METRICS
 */
export const useCustomMetricSaveMutation = (): UseMutationResult<
  CustomMetricSaveResponse,
  unknown,
  { customMetric: CustomMetricData },
  unknown
> => {
  const { saveCustomMetric } = useAdminRequests();

  return useMutation((variables: { customMetric: CustomMetricData }) => saveCustomMetric(variables.customMetric), {
    useErrorBoundary: false,
  });
};

export const useCustomMetricUpdateMutation = (): UseMutationResult<
  CustomMetricSaveResponse,
  unknown,
  { id: string; customMetric: CustomMetricData },
  unknown
> => {
  const { updateCustomMetric } = useAdminRequests();

  return useMutation(
    (variables: { id: string; customMetric: CustomMetricData }) =>
      updateCustomMetric(variables.id, variables.customMetric),
    {
      useErrorBoundary: false,
    }
  );
};

export const useCustomMetricDeleteMutation = (): UseMutationResult<
  BaseCustomMetricResponse,
  unknown,
  { id: string },
  unknown
> => {
  const { deleteCustomMetric } = useAdminRequests();

  return useMutation((variables: { id: string }) => deleteCustomMetric(variables.id), {
    useErrorBoundary: false,
  });
};

export const usePublishReportMutation = (): UseMutationResult<
  string,
  unknown,
  { publishReportData: PublishReportData },
  unknown
> => {
  const { publishCustomReport } = useAdminRequests();

  return useMutation(
    (variables: { publishReportData: PublishReportData }) => publishCustomReport(variables.publishReportData),
    {
      useErrorBoundary: false,
    }
  );
};

export const useUnpublishReportMutation = (): UseMutationResult<string, unknown, { reportId: number }, unknown> => {
  const { unpublishCustomReport } = useAdminRequests();

  return useMutation((variables: { reportId: number }) => unpublishCustomReport({ reportId: variables.reportId }), {
    useErrorBoundary: false,
  });
};

export const useUpdatePublishReportMutation = (): UseMutationResult<
  string,
  unknown,
  { updatePublishReportData: UpdatePublishReportData },
  unknown
> => {
  const { updatePublishCustomReport } = useAdminRequests();

  return useMutation(
    (variables: { updatePublishReportData: UpdatePublishReportData }) =>
      updatePublishCustomReport(variables.updatePublishReportData),
    {
      useErrorBoundary: false,
    }
  );
};

/**
 * SCHEDULE REPORTS
 */
export const useScheduleReportsSaveMutation = (): UseMutationResult<
  ScheduleReportsResponse,
  unknown,
  { scheduleReport: ScheduleReportsResponse },
  unknown
> => {
  const { saveScheduleReport } = useAdminRequests();
  return useMutation(
    (variables: { scheduleReport: ScheduleReportsResponse }) =>
      saveScheduleReport(variables.scheduleReport, variables.scheduleReport.recurrenceType ? 'recurring' : 'one-time'),
    {
      useErrorBoundary: false,
    }
  );
};

export const useDeleteScheduleMutation = (): UseMutationResult<
  DeleteScheduleResponse,
  unknown,
  { id: string },
  unknown
> => {
  const { deleteSchedule } = useAdminRequests();
  return useMutation((variables: { id: string }) => deleteSchedule(variables.id), {
    useErrorBoundary: false,
  });
};
/**
 * UPDATE SCHEDULE REPORTS
 */
export const useScheduleReportsUpdateMutation = (): UseMutationResult<
  UpdateScheduleReportsResponse,
  unknown,
  { scheduleReport: UpdateScheduleReportsResponse; scheduleId: string },
  unknown
> => {
  const { updateScheduleReport } = useAdminRequests();
  return useMutation(
    (variables: { scheduleReport: UpdateScheduleReportsResponse; scheduleId: string }) =>
      updateScheduleReport(variables.scheduleReport, variables.scheduleId),
    {
      useErrorBoundary: false,
    }
  );
};
